import { CODE_SYNTAX, CODE_SYNTAX_ATTR } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The four lines a customer pastes, shown verbatim.
 *
 * ## Why a non-developer page carries a code block at all
 *
 * The page's claim is "one line, about two minutes". A reader who has been
 * promised a two-minute install by four other vendors does not believe that
 * sentence; they believe it when they can SEE that the whole thing is a script
 * tag and a key. So the block is evidence rather than instruction — nobody has
 * to understand it, and the page says so in the caption underneath.
 *
 * It is the real embed, never an abbreviation: `docs-embed-snippet-truth.test.ts`
 * exists because a marketing snippet that has drifted from the shipped one is
 * worse than no snippet, and the customer finds out at the worst moment.
 *
 * `dir="ltr"` on the `<code>`: code is left-to-right in every locale, and an
 * Arabic or Hebrew page would otherwise reorder the attribute list into
 * something that is not the thing they need to paste.
 */
export function EmbedSnippet(): ReactElement {
  const { t } = useT();
  return (
    <figure className="mt-6">
      <div
        className="relative overflow-hidden rounded-[13px] bg-[color:var(--bd-ink)] px-5 py-4 font-mono text-[13px] leading-relaxed"
        style={{ color: CODE_SYNTAX.plain }}
      >
        <code dir="ltr" className="relative block whitespace-pre-wrap break-words">
          <span style={{ color: CODE_SYNTAX.punctuation }}>&lt;script</span>{' '}
          <span style={{ color: CODE_SYNTAX_ATTR }}>type</span>=
          <span style={{ color: CODE_SYNTAX.string }}>&quot;module&quot;</span>
          {'\n  '}
          <span style={{ color: CODE_SYNTAX_ATTR }}>src</span>=
          <span style={{ color: CODE_SYNTAX.string }}>
            &quot;https://cdn.syncanix.com/widget.js&quot;
          </span>
          {'\n  '}
          <span style={{ color: CODE_SYNTAX_ATTR }}>data-key</span>=
          <span style={{ color: CODE_SYNTAX.string }}>&quot;pk_live_…&quot;</span>
          {'\n'}
          <span style={{ color: CODE_SYNTAX.punctuation }}>&gt;&lt;/script&gt;</span>
        </code>
      </div>
      <figcaption className="mt-3 text-[0.88rem] leading-[1.55] text-[color:var(--bd-muted)]">
        {t('howItWorks.step.install.snippetCaption')}
      </figcaption>
    </figure>
  );
}
